import is from '@sindresorhus/is';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';

import { useSnackbars } from '../../../app/error-handling/SnackBars';
import type { Comment } from '../../../data-contract/comment';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';
import { allLanguageCommentsQueryKey, commentsQueryKey, commentsQueryKeyPrefix } from './use-comments-query';
import { useCurrentCommentAuthor } from './use-current-comment-author';

const COMMENT_RESOLUTION_CONFLICT_ERROR_CODE = 'JSA0018';

type UseResolveCommentParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly language: string;
    readonly version: number;
};

export type ResolveCommentVariables = {
    readonly commentId: string;
};

type ResolveCommentContext = {
    readonly hadPreviousAllLanguageCommentsQuery: boolean;
    readonly previousAllLanguageComments: readonly Comment[] | undefined;
    readonly previousComments: readonly Comment[] | undefined;
};

const withResolvedComment = (
    comments: readonly Comment[] | undefined,
    commentId: string,
    resolution: Comment['resolution'],
): readonly Comment[] | undefined => {
    if (is.undefined(comments)) return comments;
    return comments.map((comment) => (comment.id === commentId ? { ...comment, resolution } : comment));
};

const upsertComment = (comments: readonly Comment[] | undefined, comment: Comment): readonly Comment[] => {
    if (is.undefined(comments)) return [comment];
    const index = comments.findIndex((cachedComment) => cachedComment.id === comment.id);
    if (index < 0) return [...comments, comment];
    const next = [...comments];
    next[index] = comment;
    return next;
};

export const useResolveComment = ({
    architectureId,
    itemId,
    language,
    version,
}: UseResolveCommentParams): UseMutationResult<
    Comment,
    HttpFetcherError,
    ResolveCommentVariables,
    ResolveCommentContext
> => {
    const { resolveComment } = useApplicationFetchers();
    const queryClient = useQueryClient();
    const resolvedBy = useCurrentCommentAuthor();
    const { showErrorSnackbar } = useSnackbars();

    const commentsKey = commentsQueryKey({ architectureId, itemId, language, version });
    const allLanguageCommentsKey = allLanguageCommentsQueryKey({ architectureId, itemId, version });

    return useMutation<Comment, HttpFetcherError, ResolveCommentVariables, ResolveCommentContext>({
        mutationKey: ['resolve-comment', architectureId, itemId, version],
        mutationFn: async ({ commentId }) => {
            const result = await resolveComment({
                architectureId,
                body: { version },
                commentId,
                itemId,
            });
            if (result.isErr) {
                throw result.error;
            }
            return result.value;
        },
        onError: (error, _variables, context) => {
            if (error.errorCode === COMMENT_RESOLUTION_CONFLICT_ERROR_CODE) return;
            if (is.undefined(context)) return;
            queryClient.setQueryData<readonly Comment[]>(commentsKey, context.previousComments);
            if (context.hadPreviousAllLanguageCommentsQuery) {
                queryClient.setQueryData<readonly Comment[]>(
                    allLanguageCommentsKey,
                    context.previousAllLanguageComments,
                );
            } else {
                queryClient.removeQueries({ queryKey: allLanguageCommentsKey });
            }
            showErrorSnackbar(error);
        },
        onMutate: async ({ commentId }) => {
            await Promise.all([
                queryClient.cancelQueries({ queryKey: commentsKey }),
                queryClient.cancelQueries({ queryKey: allLanguageCommentsKey }),
            ]);

            const previousComments = queryClient.getQueryData<readonly Comment[]>(commentsKey);
            const previousAllLanguageComments = queryClient.getQueryData<readonly Comment[]>(allLanguageCommentsKey);
            const hadPreviousAllLanguageCommentsQuery = !is.undefined(
                queryClient.getQueryState(allLanguageCommentsKey),
            );
            const resolution: Comment['resolution'] = {
                version,
                resolvedAt: new Date().toISOString(),
                resolvedBy,
            };

            queryClient.setQueryData<readonly Comment[]>(commentsKey, (prev) =>
                withResolvedComment(prev, commentId, resolution),
            );
            queryClient.setQueryData<readonly Comment[]>(allLanguageCommentsKey, (prev) =>
                withResolvedComment(prev, commentId, resolution),
            );

            return { hadPreviousAllLanguageCommentsQuery, previousAllLanguageComments, previousComments };
        },
        onSuccess: (data, _variables, context) => {
            if (data.sourceLanguage === language) {
                queryClient.setQueryData<readonly Comment[]>(commentsKey, (prev) => upsertComment(prev, data));
            }
            if (context?.hadPreviousAllLanguageCommentsQuery === true) {
                queryClient.setQueryData<readonly Comment[]>(allLanguageCommentsKey, (prev) =>
                    upsertComment(prev, data),
                );
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: commentsQueryKeyPrefix(architectureId, itemId, version) });
        },
        throwOnError: false,
    });
};
